/**
 * Event content — risk/reward narrative nodes.
 *
 * Events are pure data: each has 2-4 choices, and each choice a list of
 * declarative OUTCOMES the api layer applies (gold/hp/card/program/trace/heal).
 * Some choices are gated (need gold / a program) and some are random — but any
 * randomness is resolved through the run RNG by the api layer, never here.
 *
 * Keeping outcomes declarative means events stay serializable + testable and the
 * balance bot can resolve them headlessly + deterministically.
 */

export type EventOutcomeKind =
  | "gold" // +/- gold (value)
  | "heal" // heal value HP
  | "loseHp" // lose value HP (can't kill — clamped to 1 by api)
  | "maxHp" // +/- max HP (value)
  | "gainCard" // add card by cardDefId to the deck
  | "gainRandomCard" // add a random reward-pool card (api rolls)
  | "gainProgram" // grant a random reward-pool program (api rolls)
  | "removeCard" // remove a chosen card (api handles selection; here it's a flag)
  | "addCurse" // add a Corruption curse to the deck
  | "addTrace" // raise trace baseline isn't possible out of combat; instead
  | "nothing"; // a pure "walk away" option

export interface EventOutcome {
  kind: EventOutcomeKind;
  value?: number;
  cardDefId?: string;
}

export interface EventChoice {
  id: string;
  label: string;
  /** Requires at least this much gold to choose (spent if `costGold`). */
  requiresGold?: number;
  costGold?: number;
  /** Requires owning any program to choose (e.g. "sacrifice a Program"). */
  requiresProgram?: boolean;
  outcomes: EventOutcome[];
  /** Flavor shown after choosing. */
  result: string;
}

export interface EventDef {
  id: string;
  name: string;
  text: string;
  choices: EventChoice[];
}

export const EVENT_DEFS: Record<string, EventDef> = {
  black_market: {
    id: "black_market",
    name: "Black Market",
    text: "A fixer offers contraband code — for a price, and a risk.",
    choices: [
      {
        id: "buy",
        label: "Buy the exploit (50 gold)",
        requiresGold: 50,
        costGold: 50,
        outcomes: [{ kind: "gainRandomCard" }],
        result: "You pocket a rare exploit.",
      },
      {
        id: "steal",
        label: "Try to steal it (risk HP)",
        outcomes: [
          { kind: "gainRandomCard" },
          { kind: "loseHp", value: 10 },
        ],
        result: "You grab the code and bolt — taking a hit on the way out.",
      },
      {
        id: "leave",
        label: "Walk away",
        outcomes: [{ kind: "nothing" }],
        result: "You keep your distance.",
      },
    ],
  },

  data_cache: {
    id: "data_cache",
    name: "Abandoned Data Cache",
    text: "An unguarded cache hums in the dark. It could be loot — or a trap.",
    choices: [
      {
        id: "crack",
        label: "Crack it open",
        outcomes: [{ kind: "gold", value: 40 }],
        result: "Clean credits. Lucky.",
      },
      {
        id: "deepscan",
        label: "Deep-scan first (lose 6 HP to its defenses)",
        outcomes: [
          { kind: "gold", value: 60 },
          { kind: "gainProgram" },
          { kind: "loseHp", value: 6 },
        ],
        result: "The defenses bite, but the haul is worth it: credits and a Program.",
      },
      {
        id: "ignore",
        label: "Leave it",
        outcomes: [{ kind: "nothing" }],
        result: "Not worth the risk.",
      },
    ],
  },

  rogue_ai: {
    id: "rogue_ai",
    name: "Rogue AI",
    text: "A fragment of a dead AI offers power in exchange for a piece of you.",
    choices: [
      {
        id: "accept",
        label: "Accept its gift (gain a Program, +1 Corruption)",
        outcomes: [{ kind: "gainProgram" }, { kind: "addCurse" }],
        result: "Power floods in — and so does the rot.",
      },
      {
        id: "bargain",
        label: "Bargain (trade 8 max HP for a card)",
        outcomes: [{ kind: "maxHp", value: -8 }, { kind: "gainRandomCard" }],
        result: "You give up a little resilience for a new tool.",
      },
      {
        id: "refuse",
        label: "Refuse and purge it",
        outcomes: [{ kind: "heal", value: 8 }],
        result: "You purge the fragment; the clean feeling restores you a little.",
      },
    ],
  },

  med_bay: {
    id: "med_bay",
    name: "Derelict Med-Bay",
    text: "Old auto-docs still have power. They can patch you — or overclock you.",
    choices: [
      {
        id: "heal",
        label: "Full repair (heal 20 HP)",
        outcomes: [{ kind: "heal", value: 20 }],
        result: "The auto-docs knit you back together.",
      },
      {
        id: "upgrade",
        label: "Cybernetic graft (+12 max HP, lose 25 gold)",
        requiresGold: 25,
        costGold: 25,
        outcomes: [{ kind: "maxHp", value: 12 }, { kind: "heal", value: 12 }],
        result: "A risky graft, but you're tougher for it.",
      },
      {
        id: "skip",
        label: "Don't trust it",
        outcomes: [{ kind: "nothing" }],
        result: "You leave the machines to rust.",
      },
    ],
  },

  purge_station: {
    id: "purge_station",
    name: "Purge Station",
    text: "A diagnostic terminal can scrub a single program from your loadout.",
    choices: [
      {
        id: "purge",
        label: "Remove a card from your deck (20 gold)",
        requiresGold: 20,
        costGold: 20,
        outcomes: [{ kind: "removeCard" }],
        result: "You scrub a card from your loadout for good.",
      },
      {
        id: "donate",
        label: "Donate to the network (lose 15 gold, heal 10 HP)",
        requiresGold: 15,
        costGold: 15,
        outcomes: [{ kind: "heal", value: 10 }],
        result: "Goodwill on the net buys you a safe patch.",
      },
      {
        id: "leave",
        label: "Move on",
        outcomes: [{ kind: "nothing" }],
        result: "You move on.",
      },
    ],
  },

  signal_ghost: {
    id: "signal_ghost",
    name: "Signal Ghost",
    text: "A looping distress signal — answering it might bring help, or attention.",
    choices: [
      {
        id: "answer",
        label: "Answer (gain a card, gain a Glitch)",
        outcomes: [{ kind: "gainRandomCard" }, { kind: "addCurse" }],
        result: "Something answers back. You got what you wanted — and a little static.",
      },
      {
        id: "trace",
        label: "Trace its source (gain 35 gold)",
        outcomes: [{ kind: "gold", value: 35 }],
        result: "You follow it to an abandoned wallet. Theirs is yours now.",
      },
      {
        id: "mute",
        label: "Mute it and move on",
        outcomes: [{ kind: "nothing" }],
        result: "Silence is safer.",
      },
    ],
  },
};

export function getEventDef(id: string): EventDef {
  const def = EVENT_DEFS[id];
  if (!def) throw new Error(`Unknown event def: ${id}`);
  return def;
}

export function allEventIds(): string[] {
  return Object.keys(EVENT_DEFS).sort();
}
